import * as React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'

interface props {
  state: boolean
  handleClose: () => void
  handleSubmit: () => void | Promise<void>
  title?: string
  text: string
  continueText?: string
  cancelText?: string
  isSubmitting?: boolean
  /** Keep the dialog open until handleSubmit finishes (long Fortnox requests). */
  closeAfterSubmit?: boolean
}

export default function ConfirmModal({
  state,
  handleClose,
  handleSubmit,
  title,
  text,
  continueText = 'Ja',
  cancelText = 'Avbryt',
  isSubmitting = false,
  closeAfterSubmit = false,
}: props) {
  const submitInFlightRef = React.useRef(false)

  React.useEffect(() => {
    if (!state) {
      submitInFlightRef.current = false
    }
  }, [state])

  const handleConfirm = async () => {
    if (submitInFlightRef.current || isSubmitting) {
      return
    }

    submitInFlightRef.current = true

    try {
      const work = Promise.resolve(handleSubmit())
      if (!closeAfterSubmit) {
        handleClose()
      }
      await work
      if (closeAfterSubmit) {
        handleClose()
      }
    } catch (error) {
      console.error('ConfirmModal submit failed', error)
    } finally {
      submitInFlightRef.current = false
    }
  }

  return (
    <Box>
      <Modal
        open={state}
        onClose={isSubmitting ? undefined : handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            maxWidth: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 5,
          }}
        >
          <Box>
            {title && (
              <Typography
                variant="h5"
                fontWeight={500}
                mb={2}
                textAlign="center"
              >
                {title}
              </Typography>
            )}
            <Typography sx={{ textAlign: 'center' }} component="p">
              {text}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 4,
                gap: 2,
              }}
            >
              <Button
                onClick={handleConfirm}
                variant="contained"
                disabled={isSubmitting}
                sx={{ color: (t) => t.palette.common.white }}
              >
                {continueText}
              </Button>
              <Button onClick={handleClose} disabled={isSubmitting}>
                {cancelText}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}
